"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.makeRefreshFetch = void 0;
/**
 * Wraps a fetch function with transparent token-refresh behaviour.
 *
 * When the inner fetch returns HTTP 401:
 *   1. Calls `refreshFn` once (the SPA posts to `auth.refresh`).
 *   2. If refresh succeeds, retries the original request once.
 *   3. If refresh fails, propagates the refresh error (the caller redirects to
 *      login).
 *
 * The three properties below are the whole contract, and they are the reason
 * this is a package with a spec of its own rather than ten lines inside
 * `src/trpc`:
 *
 *   - single-flight: concurrent 401s coalesce into ONE refresh;
 *   - at most one retry per request;
 *   - a request the caller marks with `skipRefreshFor` never triggers a refresh.
 *
 * `skipRefreshFor` marks the requests whose 401 is the ANSWER and not a symptom
 * of an expired session. The SPA marks two, for two different reasons:
 *
 *   - the REFRESH endpoint, so a refresh issued through this wrapper cannot
 *     recurse. It cannot fire as the SPA is wired today — `refreshFn` posts with
 *     a bare fetch, deliberately outside this wrapper — and it is kept because
 *     routing the refresh through the tRPC client is exactly the change that
 *     would otherwise loop forever;
 *   - the LOGIN endpoint, which does fire on every failed sign-in: a wrong
 *     password is not an expired session, and refreshing in response to it
 *     spends a round-trip only to fail and REPLACE the login's own error with
 *     the refresh's, so the form ends up saying "session expired" to a plant
 *     operator who never had one.
 *
 * Single-flight refresh: the plant screens fire several tRPC queries in parallel
 * (the session, the caller's groups, what is running on them), so an expired
 * access token makes them all 401 at once. Refreshing is idempotent — the
 * backend re-signs the tokens but does NOT rotate the user's `refreshKey` (only
 * login, logout and password change do), so parallel refreshes would not
 * invalidate one another. What they would be is N redundant round-trips against
 * a rate-limited endpoint, each followed by its own retry. So concurrent 401s
 * are coalesced into ONE in-flight refresh: the first 401 starts it and stores
 * the promise; concurrent callers await that same promise; the promise is
 * cleared once it settles so a later 401 starts a fresh refresh. Each request
 * still retries at most once.
 *
 * If the backend ever starts rotating `refreshKey` on refresh, coalescing stops
 * being an optimisation and becomes a correctness requirement: the losers of a
 * parallel refresh would hold invalidated tokens and be signed out mid-shift.
 */
const makeRefreshFetch = (innerFetch, refreshFn, opts = {}) => {
    const skipRefreshFor = opts.skipRefreshFor ?? (() => false);
    // Shared in-flight refresh promise (null when no refresh is running).
    let refreshing = null;
    const refreshOnce = () => {
        refreshing ??= refreshFn().finally(() => {
            refreshing = null;
        });
        return refreshing;
    };
    return async (url, init) => {
        const response = await innerFetch(url, init);
        // Non-401 — return as-is (including server errors)
        if (response.status !== 401) {
            return response;
        }
        // Guard: this request's 401 is its own answer — hand it back untouched
        if (skipRefreshFor(url)) {
            return response;
        }
        // 401 on a regular request — attempt a single (coalesced) refresh, then retry once
        try {
            await refreshOnce();
        }
        catch {
            // Refresh failed — propagate as UNAUTHORIZED so the app can redirect to login
            throw new Error('UNAUTHORIZED');
        }
        // refreshFn succeeded — retry the original request (at most once)
        return innerFetch(url, init);
    };
};
exports.makeRefreshFetch = makeRefreshFetch;
//# sourceMappingURL=refresh-fetch.js.map